/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { ReactNode, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import Config from 'Config';

import { FormElement, FormRow } from './form/FormLayout';

import styles from './ErrorBoundary.module.scss';

interface ErrorFallbackProps {
    error: Error;
    errorID?: string;
    resetErrorBoundary: () => void;
}

interface DefaultErrorBoundaryProps {
    children: ReactNode;
}

/**
 * This is a default error boundary component that can be used to wrap the entire application.
 * It will catch any error that occurs in the child components and display a fallback UI.
 * It also logs the error to the console and sends it to Sentry.
 * When in production, it will display a pretty UI to the user with no stack trace.
 * When in development, it will display a detailed UI to the user with stack trace and error message.
 * @param children The child components to be wrapped by the error boundary.
 * @returns The wrapped child components with error boundary.
 */
const DefaultErrorBoundary = ({ children }: DefaultErrorBoundaryProps) => {
    const [errorID, setErrorID] = useState('');

    const fallbackRender = ({
        error,
        resetErrorBoundary,
    }: {
        error: Error;
        resetErrorBoundary: () => void;
    }) => (
        <ErrorFallback
            error={error}
            resetErrorBoundary={resetErrorBoundary}
            errorID={errorID}
        />
    );
    return (
        <ErrorBoundary
            fallbackRender={fallbackRender}
            onError={(error, componentStack) => {
                console.error(error);
                Sentry.withScope(scope => {
                    scope.setExtra('componentStack', componentStack);
                    const sentryEventID = Sentry.captureException(error);
                    setErrorID(sentryEventID);
                });
            }}
        >
            {children}
        </ErrorBoundary>
    );
};

const ErrorFallback = ({
    error,
    errorID,
    resetErrorBoundary,
}: ErrorFallbackProps) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const showStackTrace = searchParams.get('showStackTrace') === 'true';
    const isDevelopment =
        import.meta.env.VITE_ENV === 'development' || showStackTrace;

    const navigateHome = () => {
        resetErrorBoundary();
        navigate('/');
    };

    const navigateBack = () => {
        resetErrorBoundary();
        navigate(-1);
    };

    const refreshPage = () => {
        window.location.reload();
    };

    const contactSupport = () => {
        window.location.href = 'tel:8002952388';
    };

    return (
        <>
            <title>Error | Motili</title>
            <div className={styles.errorBoundaryContentContainer}>
                <FormRow>
                    <FormElement>
                        <h1 className={styles.errorBoundaryHeader}>
                            Something went wrong...
                        </h1>
                    </FormElement>
                </FormRow>
                <FormRow>
                    <FormElement>
                        <h3>
                            Here are some options that might help you get back
                            on track:
                        </h3>
                    </FormElement>
                </FormRow>
                <FormRow>
                    <div className={styles.actionsContainer}>
                        <div className={styles.buttonContainer}>
                            <i className='fa fa-home' />
                            <button type='button' onClick={navigateHome}>
                                Take me home
                            </button>
                        </div>
                        <div className={styles.buttonContainer}>
                            <i className='fa fa-undo' />
                            <button type='button' onClick={navigateBack}>
                                Return to the previous page
                            </button>
                        </div>
                        <div className={styles.buttonContainer}>
                            <i className='fa fa-refresh' />
                            <button type='button' onClick={refreshPage}>
                                Refresh the page
                            </button>
                        </div>
                        <div className={styles.buttonContainer}>
                            <i className='fa fa-phone-square' />
                            <button type='button' onClick={contactSupport}>
                                Contact support for further help at:
                                <span
                                    style={{
                                        paddingLeft: '10px',
                                        color: '#57c1e9',
                                    }}
                                >
                                    800.295.2388
                                </span>
                            </button>
                        </div>
                    </div>
                </FormRow>
                {isDevelopment && (
                    <>
                        {error.stack && (
                            <pre className={styles.errorBoundaryStackTrace}>
                                {error.stack}
                            </pre>
                        )}
                        {errorID && (
                            <a
                                href={`${Config.sentry.discoveryRedirectURL}${errorID}/`}
                            >
                                View error details in Sentry
                            </a>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default DefaultErrorBoundary;
